import React, { FC, ReactElement, ReactNode, Suspense, useEffect, useMemo } from 'react';
import {
    ShowcaseSelection,
    SelectionMovie,
    MultiSelectionEntity,
    WatchingEntity,
    StyledShowcasesSelectionEntity,
    PromoSelectionItem,
} from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { ErrorBoundary, useRouter, useSignal, useWidget } from '@funtech-int/rival-react';
import { AnalyticsSenderProvider, useAnalytics, useFeatureFlags } from '@funtech-int/yango-shared';

import { useShowcaseQuery } from '@/shared/hooks/useShowcaseQuery';
import * as Showcase from '@/shared/components/Showcase';
import { filmRoute } from '@/scenarios/movie/routes';
import { useMovieModal } from '@/shared/hooks/useMovieModal';
import { selectionRoute } from '@/scenarios/selection/routes';
import { PageSpinner } from '@/shared/components/PageSpinner';
import { useProfileManager } from '@/shared/hooks/useProfileManager';
import { RemountOnKey } from '@/shared/components/RemountOnKey/RemountOnKey';
import { moviePromoBlockWidget } from '@/scenarios/movie';
import { useTimeSignal } from '@/shared/hooks/useTimeSignal';
import { channelPromoBlockWidget, channelsRoute } from '@/scenarios/channels';
import { startPlaying } from '@/scenarios/legacy-player/components/player-manager/helpers/startPlaying';
import { PlayerContentType } from '@/scenarios/legacy-player/components/feature-player/types/content';

import { featureFlagsScheme } from '../../lib/featureFlags';
import { createShowcaseAnalytics } from '../../lib/showcaseAnalytics';

import styles from './HomeShowcase.module.css';

interface HomeShowcaseProps {
    id: string;
    showLoading?: boolean;
    header?: ReactNode;
    footer?: ReactNode;
}

const ShowcaseContainer = Showcase.withVisibilityRemount<HomeShowcaseProps>((props) => {
    const { id, header, footer } = props;

    const router = useRouter();

    const { showcase, pagination } = useShowcaseQuery({ id });

    const hasSubscription = useSignal(useProfileManager().hasSubscription);
    const analytics = useAnalytics(createShowcaseAnalytics);
    analytics.setSessionId(showcase.sessionId);

    const { flags } = useFeatureFlags(featureFlagsScheme);
    const isNewPlayerEnabled = flags.get('isNewPlayerEnabled');

    useEffect(() => {
        if (hasSubscription) {
            analytics.showed();
        }
    }, [hasSubscription, analytics]);

    const movieModal = useMovieModal();

    const timeSignal = useTimeSignal(30 * 1000);

    const moviePromoBlock = useWidget(moviePromoBlockWidget);
    const channelPromoBlock = useWidget(channelPromoBlockWidget);

    const controller = useMemo(
        () => ({
            renderItem({
                item: selection,
                index,
                className,
            }: Showcase.RenderItemProps<ShowcaseSelection>) {
                if (selection.type === 'promo') {
                    const supports: PromoSelectionItem['type'][] = ['movie'];

                    if (channelPromoBlock.enabled) {
                        supports.push('channelProgram');
                    }

                    return (
                        <Showcase.PromoSelection
                            key={selection.id}
                            selection={selection}
                            supports={supports}
                            renderItem={({ item, index, next }) => {
                                const promoBlockAnalytics = analytics.createPromoBlockAnalytics(
                                    selection,
                                    index
                                );

                                let promoBlock: ReactElement;

                                if (item.type === 'channelProgram') {
                                    const onWatchClick = (contentId: string) => {
                                        startPlaying({
                                            router,
                                            contentId,
                                            contentType: PlayerContentType.CHANNEL,
                                            isNewPlayerEnabled,
                                        });
                                    };

                                    promoBlock = channelPromoBlock.render({
                                        key: item.channel.contentId,
                                        promoBlock: item,
                                        timeSignal,
                                        onWatchClick,
                                        onFinish: next,
                                        analytics: promoBlockAnalytics,
                                    });
                                } else {
                                    promoBlock = moviePromoBlock.render({
                                        key: item.id,
                                        promoBlock: item,
                                        onDislike: next,
                                        analytics: promoBlockAnalytics,
                                    });
                                }

                                return <div className={styles.promoBlock}>{promoBlock}</div>;
                            }}
                        />
                    );
                }

                if (selection.type === 'cw') {
                    const handleCardShow = (entity: WatchingEntity, index: number) => {
                        analytics.continueWatchingCardShowed(selection, entity, index);
                    };

                    const handleCardClick = (entity: WatchingEntity, index: number) => {
                        analytics.continueWatchingCardNavigated(selection, entity, index);
                    };

                    return (
                        <Showcase.ContinueWatching
                            key={selection.id}
                            movieRoute={filmRoute}
                            id={selection.id}
                            showcaseId={selection.showcaseId}
                            className={className}
                            onCardShow={handleCardShow}
                            onCardClick={handleCardClick}
                        />
                    );
                }

                if (selection.type === 'multi') {
                    const handleCardShow = (entity: MultiSelectionEntity, entityIndex: number) => {
                        analytics.multiSelectionCardShowed(selection, index, entity, entityIndex);
                    };

                    const handleCardClick = (entity: MultiSelectionEntity, entityIndex: number) => {
                        analytics.multiSelectionCardNavigated(
                            selection,
                            index,
                            entity,
                            entityIndex
                        );
                    };

                    return (
                        <Showcase.MultiSelection
                            key={selection.id}
                            selection={selection}
                            selectionRoute={selectionRoute}
                            onCardShow={handleCardShow}
                            onCardClick={handleCardClick}
                            className={className}
                        />
                    );
                }

                if (selection.type === 'showcases') {
                    const handleCardShow = (
                        entity: StyledShowcasesSelectionEntity,
                        entityIndex: number
                    ) => {
                        analytics.styledShowcaseCardShowed(selection, index, entity, entityIndex);
                    };

                    const handleCardClick = (
                        entity: StyledShowcasesSelectionEntity,
                        entityIndex: number
                    ) => {
                        analytics.styledShowcaseCardNavigated(
                            selection,
                            index,
                            entity,
                            entityIndex
                        );
                    };

                    return (
                        <Showcase.StyledShowcase
                            key={selection.id}
                            selection={selection}
                            onCardShow={handleCardShow}
                            onCardClick={handleCardClick}
                            className={className}
                        />
                    );
                }

                if (selection.type === 'channelPrograms') {
                    const channelProgramsAnalytics = analytics.createChannelProgramsAnalytics(
                        selection,
                        index
                    );

                    const startContent = (contentId: string) => {
                        startPlaying({
                            router,
                            contentId,
                            contentType: PlayerContentType.CHANNEL,
                            isNewPlayerEnabled,
                        });
                    };

                    return (
                        <Showcase.ChannelPrograms
                            key={selection.id}
                            selection={selection}
                            location={channelsRoute()}
                            channelRoute={channelsRoute}
                            timeSignal={timeSignal}
                            className={className}
                            startContent={startContent}
                            onHeaderClick={() =>
                                channelProgramsAnalytics.selectionNavigated('header')
                            }
                            onShowAllClick={() =>
                                channelProgramsAnalytics.selectionNavigated('last_item')
                            }
                            onCardShow={channelProgramsAnalytics.channelImpressionShowed}
                            onCardClick={channelProgramsAnalytics.channelImpressionNavigated}
                        />
                    );
                }

                const handleHeaderClick = () => {
                    analytics.selectionNavigated(selection, index, 'header');
                };

                const handleShowAllClick = () => {
                    analytics.selectionNavigated(selection, index, 'last_item');
                };

                const handleCardShow = (movie: SelectionMovie, movieIndex: number) => {
                    analytics.cardShowed(selection, index, movie, movieIndex);
                };

                const handleCardClick = (movie: SelectionMovie, movieIndex: number) => {
                    analytics.cardClicked(selection, index, movie, movieIndex);
                    movieModal.open(movie.contentId);
                };

                if (selection.type === 'default') {
                    return (
                        <Showcase.Selection
                            key={selection.id}
                            selection={selection}
                            location={selectionRoute({ id: selection.id })}
                            movieRoute={filmRoute}
                            onHeaderClick={handleHeaderClick}
                            onShowAllClick={handleShowAllClick}
                            onCardShow={handleCardShow}
                            onCardClick={handleCardClick}
                            className={className}
                        />
                    );
                }

                if (selection.type === 'top10') {
                    return (
                        <Showcase.Top10
                            key={selection.id}
                            selection={selection}
                            movieRoute={filmRoute}
                            onCardClick={handleCardClick}
                            className={className}
                        />
                    );
                }

                if (selection.type === 'originals') {
                    return (
                        <Showcase.Originals
                            key={selection.id}
                            selection={selection}
                            location={selectionRoute({ id: selection.id })}
                            movieRoute={filmRoute}
                            onHeaderClick={handleHeaderClick}
                            onShowAllClick={handleShowAllClick}
                            onCardShow={handleCardShow}
                            onCardClick={handleCardClick}
                            className={className}
                        />
                    );
                }

                return null;
            },
        }),
        [
            analytics,
            channelPromoBlock,
            isNewPlayerEnabled,
            movieModal,
            moviePromoBlock,
            timeSignal,
            router,
        ]
    );

    return (
        <>
            {header}
            <Showcase.Layout
                items={showcase.selections}
                renderItem={controller.renderItem}
                pagination={pagination}
            />
            {footer}
        </>
    );
});

export const HomeShowcase: FC<HomeShowcaseProps> = ({ id, header, footer, showLoading }) => (
    <AnalyticsSenderProvider>
        <RemountOnKey mountKey={id}>
            <ErrorBoundary fallback={() => null}>
                <Suspense fallback={showLoading ? <PageSpinner absoluteCenter noOffset /> : null}>
                    <ShowcaseContainer id={id} header={header} footer={footer} />
                </Suspense>
            </ErrorBoundary>
        </RemountOnKey>
    </AnalyticsSenderProvider>
);
